import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Flame, Loader2, Coins, Gift, CalendarCheck } from "lucide-react";
import confetti from "canvas-confetti";
import DraftBoard from "../components/DraftBoard";
import LiveSimulation from "../components/LiveSimulation";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Toaster } from "../components/ui/toaster";
import { auth, dailyApi, summaryFrom } from "../lib/api";
import { pepSeasonVs, PEP } from "../data/gameData";
import { buildDaily, toCompact, themeName } from "../data/dailyClash";
import { untilReset } from "../components/DailyClashCard";

const Bar = ({ navigate, streak }) => (
  <div className="sticky top-0 z-40 bg-[#0F172A]/85 backdrop-blur-md border-b border-white/5">
    <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
      <button data-testid="daily-back-btn" onClick={() => navigate("/")} className="pill-nav rounded-full px-3.5 py-1.5 flex items-center gap-2 text-sm font-medium text-slate-200">
        <ArrowLeft className="w-4 h-4" /> Home
      </button>
      <div className="font-display font-extrabold text-sm tracking-tight">Daily <span className="text-[#F59E0B]">Clash</span></div>
      <div data-testid="daily-bar-streak" className="flex items-center gap-1 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/40 px-3 py-1.5 text-sm font-bold text-[#F59E0B]">
        <Flame className="w-4 h-4" /> {streak}
      </div>
    </div>
  </div>
);

const OUTCOME = { win: "You beat Pep!", draw: "Honours even", loss: "Pep wins today" };

const DoneCard = ({ d, navigate }) => (
  <div data-testid="daily-done" className="relative z-10 max-w-sm mx-auto px-5 py-12 text-center">
    <div className="mx-auto w-16 h-16 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/40 flex items-center justify-center">
      <CalendarCheck className="w-8 h-8 text-[#F59E0B]" />
    </div>
    <h1 className="font-display font-extrabold text-3xl mt-4">{OUTCOME[d.result?.outcome] || "Done for today"}</h1>
    {d.result && (
      <p className="text-slate-300 mt-2 text-sm">
        {d.result.my_points} pts vs Pep's {d.result.their_points} · {d.result.reward ? `+£${d.result.reward}` : "no winnings today"}
      </p>
    )}
    <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
      <div className="flex items-center justify-center gap-2 font-display font-extrabold text-4xl text-[#F59E0B]"><Flame className="w-8 h-8" /> {d.streak}</div>
      <div className="text-xs text-slate-400 mt-1">day streak · best {d.best_streak}</div>
      <div data-testid="daily-next-reset" className="text-sm text-white mt-3">New clash in {untilReset(d.next_reset)}</div>
      <div className="text-[11px] text-slate-500 mt-1">Miss a day and the streak resets.</div>
    </div>
    <button data-testid="daily-home-btn" onClick={() => navigate("/")} className="btn-green w-full rounded-xl py-3.5 mt-6 font-bold">Back to Home</button>
  </div>
);

const Intro = ({ d, puzzle }) => (
  <div className="max-w-5xl mx-auto px-4 pt-4">
    <div className="card-38 p-4 flex items-center gap-3 hover:translate-y-0">
      <img src={PEP.avatar} alt={PEP.name} className="w-12 h-12 rounded-full object-cover border border-[#F59E0B]/50 shrink-0" />
      <div className="min-w-0">
        <div data-testid="daily-theme" className="font-semibold text-white text-sm">Today: Pep manages {themeName(puzzle.theme)}</div>
        <div className="text-muted-38 text-xs">
          Formation locked at {puzzle.formationLabel}. {11 - puzzle.openCount} players are signed — draft the last {puzzle.openCount}. Win £{d.reward_win}, draw £{d.reward_draw}.
          {d.streak < d.free_spin_streak ? ` Hit a ${d.free_spin_streak}-day streak for a free daily spin.` : " Your streak earns a free spin every day."}
        </div>
      </div>
    </div>
  </div>
);

const RewardModal = ({ reward, onClose }) => (
  <Dialog open={!!reward} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="daily-reward-modal" className="bg-[#1E293B] border border-[#F59E0B]/40 text-white w-[calc(100%-2rem)] max-w-xs p-6 text-center rounded-2xl">
      <DialogTitle className="font-display font-extrabold text-2xl">{OUTCOME[reward?.outcome]}</DialogTitle>
      <DialogDescription className="text-[#10B981] font-display font-bold text-lg flex items-center justify-center gap-1.5">
        <Coins className="w-5 h-5" /> +£{reward?.reward || 0}
      </DialogDescription>
      <div className="flex items-center justify-center gap-2 font-display font-extrabold text-3xl text-[#F59E0B] mt-2"><Flame className="w-7 h-7" /> {reward?.streak}</div>
      <div className="text-xs text-slate-400">day streak</div>
      {reward?.unlocked_free_spin && (
        <div data-testid="daily-free-spin-unlocked" className="mt-3 rounded-xl bg-[#F59E0B]/15 border border-[#F59E0B]/40 px-3 py-2 text-sm text-[#F59E0B] font-semibold flex items-center justify-center gap-2">
          <Gift className="w-4 h-4" /> Free daily spin unlocked!
        </div>
      )}
      <button data-testid="daily-reward-close" onClick={onClose} className="btn-green w-full rounded-xl py-3 mt-4 font-semibold">Nice one</button>
    </DialogContent>
  </Dialog>
);

const DailyClash = () => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [user, setUser] = useState(null);
  const [result, setResult] = useState(null);
  const [reward, setReward] = useState(null);
  const puzzle = useMemo(() => (d ? buildDaily(d.seed) : null), [d]);

  useEffect(() => {
    auth.ensure().then((u) => { setUser(u); return dailyApi.get(); }).then(setD).catch(() => setD(null));
  }, []);

  if (!d || !puzzle) {
    return <div className="bg-app min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const onSimulate = (myResult, capXi) => {
    const pepXi = puzzle.pepXi;
    const me = { email: "me", username: user?.username || "You", summary: summaryFrom(myResult), board: { formation: puzzle.formation, filled: 11, xi: capXi.map(toCompact) } };
    const pep = { email: "pep", username: PEP.name, avatar: PEP.avatar, summary: summaryFrom(pepSeasonVs(myResult.points, pepXi)), board: { formation: puzzle.formation, filled: 11, xi: pepXi.map(toCompact) } };
    const winner = me.summary.points > pep.summary.points ? "me" : pep.summary.points > me.summary.points ? "pep" : "tie";
    setResult({ creator: me, opponent: pep, winner, status: "complete", stake: 0, pot: 0 });
  };

  const onComplete = async () => {
    const outcome = result.winner === "me" ? "win" : result.winner === "tie" ? "draw" : "loss";
    try {
      const res = await dailyApi.result({ outcome, my_points: result.creator.summary.points, their_points: result.opponent.summary.points, theme: themeName(puzzle.theme) });
      setReward({ ...res, outcome });
      setD(res);
      if (outcome === "win") confetti({ particleCount: 150, spread: 95, origin: { y: 0.5 }, colors: ["#F59E0B", "#10B981", "#ffffff"], zIndex: 100000 });
    } catch (e) { /* already played */ }
  };

  return (
    <div className="bg-app min-h-screen text-white relative overflow-hidden">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <Bar navigate={navigate} streak={d.streak} />
      {d.played && !result ? (
        <DoneCard d={d} navigate={navigate} />
      ) : result ? (
        <LiveSimulation match={result} user={{ email: "me", username: result.creator.username }} friendly againLabel="Back to Home" onPlayAgain={() => navigate("/")} onComplete={onComplete} />
      ) : (
        <>
          <Intro d={d} puzzle={puzzle} />
          <DraftBoard initialXi={puzzle.myXi} initialFormation={puzzle.formation} onSimulate={onSimulate} ctaLabel="Play the Daily Clash" subtitle={`Spin a club and sign your last ${puzzle.openCount} players.`} />
        </>
      )}
      <RewardModal reward={reward} onClose={() => setReward(null)} />
      <Toaster />
    </div>
  );
};

export default DailyClash;
